// Referral Program API service for interacting with backend
import axios from 'axios';
import { cookieManager } from '../utils/cookieManager';
import { getBaseApiUrl, getCorsSettings } from '../utils/apiConfig';
import { handle401Error } from '../utils/handle401Error';
import { HEADER_KEYS, STORAGE_KEYS } from '../constants/identity';

// Base API configuration
const API_BASE_URL = getBaseApiUrl();
const corsSettings = getCorsSettings();

const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
  ...corsSettings,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add request interceptor for authentication
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem(STORAGE_KEYS.AUTH_TOKEN);
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // Add user ID header from authenticated user or localstorage
    const userId = localStorage.getItem(STORAGE_KEYS.USER_ID);
    if (userId) {
      config.headers[HEADER_KEYS.USER_ID] = userId;
    }

    // Add UUID from cookie for user recognition
    const userUuid = cookieManager.getUserUuid();
    if (userUuid) {
      config.headers[HEADER_KEYS.USER_UUID] = userUuid;
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add response interceptor for error handling
api.interceptors.response.use(
  (response) => response,
  (error) => {
    // Handle 401 authentication errors
    if (error.response?.status === 401) {
      handle401Error();
      return Promise.reject(error);
    }

    console.error('Referral API Error:', error.response?.data || error.message);
    return Promise.reject(error);
  }
);

export const referralService = {
  // ===== GET Endpoints =====

  /**
   * Get user's referral code
   * @param {string} userId - User UUID
   * @returns {Promise<{code: string}>}
   */
  async getReferralCode(userId) {
    try {
      const response = await api.get('/referrals', {
        params: {
          userId: userId,
          type: 'code'
        }
      });
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.error || 'Failed to get referral code');
    }
  },

  /**
   * Get referral statistics for dashboard
   * @param {string} userId - User UUID
   * @returns {Promise<{stats: object}>}
   */
  async getReferralStats(userId) {
    try {
      const response = await api.get('/referrals', {
        params: {
          userId: userId,
          type: 'stats'
        }
      });
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.error || 'Failed to get referral statistics');
    }
  },

  /**
   * Get referral history with sorting and filtering
   * @param {string} userId - User UUID
   * @param {string} sortOrder - 'newest_first' | 'oldest_first' | 'status'
   * @param {string} filter - 'all' | 'pending' | 'qualified' | 'rewarded' | 'expired'
   * @returns {Promise<{history: array, total: number}>}
   */
  async getReferralHistory(userId, sortOrder = 'newest_first', filter = 'all') {
    try {
      const response = await api.get('/referrals', {
        params: {
          userId: userId,
          type: 'history',
          sortOrder: sortOrder,
          filter: filter
        }
      });
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.error || 'Failed to get referral history');
    }
  },

  /**
   * Get pending rewards balance
   * @param {string} userId - User UUID
   * @returns {Promise<{count: number, amountUsd: number, amountCents: number}>}
   */
  async getPendingRewards(userId) {
    try {
      const response = await api.get('/referrals', {
        params: {
          userId: userId,
          type: 'pending'
        }
      });
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.error || 'Failed to get pending rewards');
    }
  },

  // ===== POST Endpoints =====

  /**
   * Generate referral code for a user (called during signup)
   * @param {string} userId - User UUID
   * @param {string} userEmail - User email address
   * @returns {Promise<{success: boolean, code: string}>}
   */
  async generateReferralCode(userId, userEmail) {
    try {
      const response = await api.post('/referrals', {
        action: 'generate',
        userId: userId,
        userEmail: userEmail
      });
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.error || 'Failed to generate referral code');
    }
  },

  /**
   * Validate a referral code and return referrer information
   * @param {string} code - Referral code to validate
   * @returns {Promise<{valid: boolean, referrerUserId?: string, sanitizedCode?: string, error?: string}>}
   */
  async validateReferralCode(code) {
    try {
      const response = await api.post('/referrals', {
        action: 'validate',
        code: code
      });
      return response.data;
    } catch (error) {
      // For validation, we want to return the response even if it's a 200 with valid: false
      if (error.response?.status === 200) {
        return error.response.data;
      }
      throw new Error(error.response?.data?.error || 'Failed to validate referral code');
    }
  },

  /**
   * Claim pending referral rewards (creates bonus allocation)
   * @param {string} userId - User UUID
   * @returns {Promise<{success: boolean, allocationId: string, amountClaimed: number}>}
   */
  async claimPendingRewards(userId) {
    try {
      const response = await api.post('/referrals', {
        action: 'claim',
        userId: userId
      });
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.error || 'Failed to claim referral rewards');
    }
  },

  /**
   * Create referral tracking record (internal use, called during signup)
   * @param {string} referrerUserId - Referrer's user UUID
   * @param {string} refereeUserId - Referee's user UUID
   * @param {string} referralCode - Referral code used
   * @param {string} refereeSignupIp - Referee's IP address (optional)
   * @param {string} cookieSetAt - Cookie timestamp (optional, ISO string)
   * @returns {Promise<{success: boolean, referralId: string}>}
   */
  async createReferralTracking(referrerUserId, refereeUserId, referralCode, refereeSignupIp = null, cookieSetAt = null) {
    try {
      const requestData = {
        action: 'create_tracking',
        referrerUserId: referrerUserId,
        refereeUserId: refereeUserId,
        referralCode: referralCode
      };

      if (refereeSignupIp) {
        requestData.refereeSignupIp = refereeSignupIp;
      }

      if (cookieSetAt) {
        requestData.cookieSetAt = cookieSetAt;
      }

      const response = await api.post('/referrals', requestData);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.error || 'Failed to create referral tracking');
    }
  },

  // ===== Custom Code Endpoints =====

  /**
   * Request a custom referral code (requires admin approval)
   * @param {string} userId - User UUID
   * @param {string} requestedCode - Desired custom code
   * @returns {Promise<{success: boolean, requestId: string, message: string}>}
   */
  async requestCustomCode(userId, requestedCode) {
    try {
      const response = await api.post('/referrals', {
        action: 'request_custom_code',
        userId: userId,
        requestedCode: requestedCode
      });
      return response.data;
    } catch (error) {
      // Return error details for UI to show appropriate message
      const errorData = error.response?.data || {};
      return {
        success: false,
        error: errorData.error || 'Failed to submit code request',
        nextAllowedAt: errorData.nextAllowedAt || null
      };
    }
  },

  /**
   * Get current custom code request status
   * @param {string} userId - User UUID
   * @returns {Promise<{success: boolean, request: object|null}>}
   */
  async getCustomCodeRequestStatus(userId) {
    try {
      const response = await api.get('/referrals', {
        params: {
          userId: userId,
          type: 'custom_code_status'
        }
      });
      return response.data;
    } catch (error) {
      return {
        success: false,
        request: null,
        error: error.response?.data?.error || 'Failed to get request status'
      };
    }
  },

  /**
   * Cancel a pending custom code request
   * @param {string} userId - User UUID
   * @param {string} requestId - Request UUID
   * @returns {Promise<{success: boolean, message: string}>}
   */
  async cancelCustomCodeRequest(userId, requestId) {
    try {
      const response = await api.post('/referrals', {
        action: 'cancel_custom_code_request',
        userId: userId,
        requestId: requestId
      });
      return response.data;
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to cancel request'
      };
    }
  },

  /**
   * Check if a custom code is available (real-time lookup)
   * @param {string} userId - User UUID
   * @param {string} code - Code to check
   * @returns {Promise<{available: boolean, reason?: string}>}
   */
  async checkCodeAvailability(userId, code) {
    try {
      const response = await api.get('/referrals', {
        params: {
          userId: userId,
          type: 'check_code_availability',
          code: code
        }
      });
      return response.data;
    } catch (error) {
      return {
        available: false,
        reason: error.response?.data?.error || 'Failed to check availability'
      };
    }
  }
};

// Helper functions for referral program
export const referralHelpers = {
  /**
   * Format referral code for display
   * @param {string} code - Referral code
   * @returns {string} Formatted code
   */
  formatReferralCode(code) {
    return code ? code.toLowerCase() : '';
  },

  /**
   * Generate shareable referral link
   * @param {string} code - Referral code
   * @returns {string} Full URL for sharing
   */
  generateReferralLink(code) {
    const baseUrl = window.location.origin;
    return `${baseUrl}/r/${this.formatReferralCode(code)}`;
  },

  /**
   * Generate QR code URL for referral link
   * @param {string} code - Referral code
   * @returns {string} QR code image URL
   */
  generateQRCodeUrl(code) {
    const referralLink = this.generateReferralLink(code);
    // Using QR Server API for QR code generation
    return `https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(referralLink)}`;
  },

  /**
   * Format referral status for display
   * @param {string} status - Raw status from API
   * @returns {object} Display info with color and label
   */
  formatReferralStatus(status) {
    const statusMap = {
      pending: {
        label: 'Pending',
        color: 'yellow',
        description: 'Waiting for qualification'
      },
      qualified: {
        label: 'Qualified',
        color: 'green',
        description: 'Ready to claim'
      },
      rewarded: {
        label: 'Claimed',
        color: 'blue',
        description: 'Reward claimed'
      },
      expired: {
        label: 'Expired',
        color: 'gray',
        description: 'Qualification window expired'
      }
    };

    return statusMap[status] || statusMap.pending;
  },

  /**
   * Calculate referral reward amount based on count
   * @param {number} count - Number of qualified referrals
   * @returns {number} Total reward amount in USD
   */
  calculateRewardAmount(count) {
    const REFERRER_REWARD_AMOUNT = 5.00;
    return count * REFERRER_REWARD_AMOUNT;
  },

  /**
   * Format currency for display
   * @param {number} amount - Amount in USD
   * @returns {string} Formatted currency string
   */
  formatCurrency(amount) {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  },

  /**
   * Get referral program rules
   * @returns {array} List of program rules
   */
  getProgramRules() {
    return [
      {
        title: 'Share Your Code',
        description: 'Share your unique referral code or link with friends and family',
        icon: '🔗'
      },
      {
        title: 'Friend Gets $10',
        description: 'Your friend receives a $10 credit when they sign up using your code',
        icon: '🎁'
      },
      {
        title: 'They Qualify',
        description: 'Your friend must complete their first billing cycle and use 1GB of data (or $25 in payments for Pay-as-You-Go)',
        icon: '✅'
      },
      {
        title: 'You Earn $5',
        description: 'Once your friend qualifies, you can claim a $5 reward for each qualified referral',
        icon: '💰'
      },
      {
        title: 'Unlimited Referrals',
        description: 'There\'s no limit to how many friends you can refer or how much you can earn',
        icon: '🚀'
      }
    ];
  },

  /**
   * Validate referral code format (client-side)
   * @param {string} code - Referral code to validate
   * @returns {object} Validation result
   */
  validateCodeFormat(code) {
    if (!code || code.trim() === '') {
      return {
        valid: false,
        error: 'Referral code is required'
      };
    }

    const sanitized = code.trim().toLowerCase();

    if (sanitized.length < 4) {
      return {
        valid: false,
        error: 'Referral code must be at least 4 characters'
      };
    }

    if (sanitized.length > 24) {
      return {
        valid: false,
        error: 'Referral code must be at most 24 characters'
      };
    }

    if (!/^[a-z0-9_]+$/.test(sanitized)) {
      return {
        valid: false,
        error: 'Referral code can only contain letters, numbers, and underscores'
      };
    }

    return {
      valid: true,
      sanitizedCode: sanitized
    };
  },

  /**
   * Copy text to clipboard
   * @param {string} text - Text to copy
   * @returns {Promise<boolean>} Success status
   */
  async copyToClipboard(text) {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch (error) {
      console.error('Failed to copy to clipboard:', error);
      return false;
    }
  },

  /**
   * Store referral code in session for signup flow
   * @param {string} code - Referral code
   * @param {string} referrerUserId - Referrer's user ID
   */
  storeReferralForSignup(code, referrerUserId) {
    const referralData = {
      code: code,
      referrerUserId: referrerUserId,
      timestamp: Date.now(),
      cookieSetAt: new Date().toISOString()
    };
    sessionStorage.setItem('referral_signup', JSON.stringify(referralData));

    // Also store as a cookie for better persistence
    const expiryDate = new Date();
    expiryDate.setDate(expiryDate.getDate() + 30); // 30 days
    document.cookie = `referral_code=${code}; expires=${expiryDate.toUTCString()}; path=/`;
  },

  /**
   * Retrieve referral data for signup flow
   * @returns {object|null} Referral data or null
   */
  getReferralForSignup() {
    try {
      const stored = sessionStorage.getItem('referral_signup');
      if (!stored) {
        // Try to get from cookie as fallback
        const cookieValue = document.cookie
          .split('; ')
          .find(row => row.startsWith('referral_code='))
          ?.split('=')[1];

        if (cookieValue) {
          return {
            code: cookieValue,
            referrerUserId: null, // Will need to validate again
            timestamp: Date.now(),
            cookieSetAt: new Date().toISOString()
          };
        }
        return null;
      }

      const data = JSON.parse(stored);

      // Check if data is less than 30 days old
      const thirtyDays = 30 * 24 * 60 * 60 * 1000;
      if (Date.now() - data.timestamp > thirtyDays) {
        this.clearReferralForSignup();
        return null;
      }

      return data;
    } catch (error) {
      console.error('Error retrieving referral data:', error);
      this.clearReferralForSignup();
      return null;
    }
  },

  /**
   * Clear stored referral data
   */
  clearReferralForSignup() {
    sessionStorage.removeItem('referral_signup');
    document.cookie = 'referral_code=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
  },

  // ===== Custom Code Helpers =====

  /**
   * Validate custom code format (client-side)
   * @param {string} code - Code to validate
   * @returns {object} Validation result with detailed errors
   */
  validateCustomCodeFormat(code) {
    const errors = [];
    const sanitized = code.trim().toLowerCase();

    if (sanitized.length < 4) {
      errors.push('Must be at least 4 characters');
    }
    if (sanitized.length > 24) {
      errors.push('Must be at most 24 characters');
    }
    if (!/^[a-z0-9_]*$/.test(sanitized)) {
      errors.push('Only lowercase letters, numbers, and underscores allowed');
    }
    if (sanitized.startsWith('_') || sanitized.endsWith('_')) {
      errors.push('Cannot start or end with underscore');
    }
    if (sanitized.includes('__')) {
      errors.push('Cannot contain consecutive underscores');
    }

    return {
      valid: errors.length === 0,
      errors: errors,
      sanitizedCode: sanitized
    };
  },

  /**
   * Get custom code rules for display
   * @returns {array} List of rules
   */
  getCustomCodeRules() {
    return [
      'Lowercase letters, numbers, and underscores only',
      'Cannot start or end with underscore',
      'Cannot contain consecutive underscores',
      'Your old code will be reserved for 30 days'
    ];
  },

  /**
   * Format custom code request status for display
   * @param {string} status - Request status
   * @returns {object} Status display info
   */
  formatCustomCodeRequestStatus(status) {
    const statusMap = {
      pending: {
        label: 'Pending Approval',
        color: 'yellow',
        description: 'Waiting for admin review'
      },
      approved: {
        label: 'Approved',
        color: 'green',
        description: 'Code change completed'
      },
      rejected: {
        label: 'Rejected',
        color: 'red',
        description: 'Request was declined'
      }
    };

    return statusMap[status] || statusMap.pending;
  }
};

export default referralService;
